import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  createAgentConfig,
  deleteAgentConfig,
  fetchAgentConfig,
  fetchAgentConfigs,
  fetchConvention,
  revokeAgentApiKey,
  updateAgentConfig,
  type AgentConfig,
} from '../../lib/api';
import { isApiError } from '../../lib/api/error';
import { queryKeys } from './queryKeys';

type CreateAgentConfigInput = {
  name: string;
};

export const useAgentConfigs = (
  token: string | null,
  projectId: string,
  options?: { includeDeleted?: boolean; enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.agentConfigs.all(projectId),
    queryFn: () => fetchAgentConfigs(token as string, projectId, { includeDeleted: true }),
    enabled: Boolean(token) && projectId.length > 0 && (options?.enabled ?? true),
    staleTime: 180_000,
    // 러너 모달 재오픈·프로젝트 전환 대응: 캐시 오래 유지 + 이전 목록 즉시 표시.
    gcTime: 1_800_000,
    placeholderData: keepPreviousData,
  });
};

/**
 * 현재 멤버가 만든 active AgentConfig id 목록. 코멘트/답글 삭제 권한 판정에 쓴다.
 * 서버 canRemoveComment는 "작성자 본인 또는 그 코멘트를 쓴 에이전트를 만든 멤버"를 허용하므로
 * (api/src/routes/comments/index.ts), 화면도 같은 축을 CommentTimeline에 넘겨야 메뉴가 맞는다.
 */
export const useCurrentMemberAgentIds = (
  token: string | null,
  projectId: string,
  currentMemberId: string | null,
  options?: { enabled?: boolean },
): string[] => {
  const { data: agentConfigs } = useAgentConfigs(token, projectId, {
    enabled: currentMemberId != null && (options?.enabled ?? true),
  });

  return useMemo(
    () =>
      currentMemberId == null
        ? []
        : (agentConfigs ?? [])
            .filter((agentConfig) => agentConfig.deletedAt === null)
            .filter((agentConfig) => agentConfig.createdByMemberId === currentMemberId)
            .map((agentConfig) => agentConfig.id),
    [agentConfigs, currentMemberId],
  );
};

/**
 * 접근 가능한 여러 프로젝트에 걸쳐 active(미삭제) AgentConfig가
 * 1개라도 있는지 판단한다. AgentConfig는 CLI authorize(`agentteams init`)
 * 흐름에서만 생성되므로, 온보딩의 "CLI 프로젝트 연결 완료" 판단에 사용한다.
 * 첫 프로젝트가 아닌 곳에 설정이 있어도 완료로 잡히도록 전체 프로젝트를 본다.
 */
export const useHasActiveAgentConfig = (
  token: string | null,
  projectIds: string[],
  options?: { enabled?: boolean; refetchOnWindowFocus?: boolean },
): { hasActive: boolean; isLoading: boolean } => {
  const enabled = Boolean(token) && (options?.enabled ?? true);
  const queries = useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: queryKeys.agentConfigs.all(projectId),
      queryFn: () => fetchAgentConfigs(token as string, projectId, { includeDeleted: true }),
      enabled: enabled && projectId.length > 0,
      refetchOnWindowFocus: options?.refetchOnWindowFocus,
      staleTime: 180_000,
    })),
  });

  const hasActive = queries.some((query) => (query.data ?? []).some((config) => config.deletedAt === null));
  const isLoading = queries.some((query) => query.isLoading);

  return { hasActive, isLoading };
};

export const useAgentConfig = (
  token: string | null,
  projectId: string,
  id: string,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.agentConfigs.detail(projectId, id),
    queryFn: () => fetchAgentConfig(token as string, projectId, id),
    enabled: Boolean(token) && projectId.length > 0 && (options?.enabled ?? true),
    staleTime: 180_000,
  });
};

export const useConvention = (token: string | null, projectId: string, id: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.agentConfigs.convention(projectId, id),
    queryFn: () => fetchConvention(token as string, projectId, id),
    enabled: Boolean(token) && projectId.length > 0 && (options?.enabled ?? true),
    staleTime: 180_000,
  });
};

export const useCreateAgentConfigMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAgentConfigInput) => createAgentConfig(token as string, projectId, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.agentConfigs.all(projectId) });
      const previous = queryClient.getQueryData<AgentConfig[]>(queryKeys.agentConfigs.all(projectId));

      const optimisticConfig: AgentConfig = {
        id: `temp-${Date.now()}`,
        name: input.name,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deletedAt: null,
      };

      queryClient.setQueryData<AgentConfig[]>(queryKeys.agentConfigs.all(projectId), (current) => [
        optimisticConfig,
        ...(current ?? []),
      ]);

      return { previous };
    },
    onError: (_error, _input, context) => {
      queryClient.setQueryData(queryKeys.agentConfigs.all(projectId), context?.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentConfigs.all(projectId) });
    },
  });
};

export const useUpdateAgentConfigMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: {
        targetDaemonId?: string | null;
        planModeOnly?: boolean;
        useWorktree?: boolean;
        shared?: boolean;
      };
    }) => updateAgentConfig(token as string, projectId, id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentConfigs.all(projectId) });
    },
  });
};

export const useDeleteAgentConfigMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      try {
        await deleteAgentConfig(token as string, projectId, id);
      } catch (error) {
        if (isApiError(error) && error.statusCode === 404) {
          return;
        }
        throw error;
      }
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.agentConfigs.all(projectId) });

      const previous = queryClient.getQueryData<AgentConfig[]>(queryKeys.agentConfigs.all(projectId));
      const now = new Date().toISOString();
      queryClient.setQueryData<AgentConfig[]>(queryKeys.agentConfigs.all(projectId), (current) =>
        current
          ? current.map((config) => (config.id === id ? { ...config, deletedAt: now, updatedAt: now } : config))
          : current,
      );
      queryClient.removeQueries({ queryKey: queryKeys.agentConfigs.convention(projectId, id) });

      return { previous, id };
    },
    onError: (_error, _id, context) => {
      queryClient.setQueryData(queryKeys.agentConfigs.all(projectId), context?.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentConfigs.all(projectId) });
    },
  });
};

/**
 * 레거시 API 키 폐기.
 *
 * 폐기는 서버 레코드 하나만 바꾸지만 화면에서는 배지 하나의 소멸로 보이므로, 목록 캐시를
 * 낙관적으로 먼저 접는다. 이미 폐기된 키에 대한 404는 목표 상태가 이미 이뤄진 것이므로
 * 성공으로 접는다(삭제 뮤테이션과 같은 규칙).
 */
export const useRevokeAgentApiKeyMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      try {
        await revokeAgentApiKey(token as string, projectId, id);
      } catch (error) {
        if (isApiError(error) && error.statusCode === 404) {
          return;
        }
        throw error;
      }
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.agentConfigs.all(projectId) });

      const previous = queryClient.getQueryData<AgentConfig[]>(queryKeys.agentConfigs.all(projectId));
      const now = new Date().toISOString();
      queryClient.setQueryData<AgentConfig[]>(queryKeys.agentConfigs.all(projectId), (current) =>
        current
          ? current.map((config) => (config.id === id ? { ...config, hasApiKey: false, apiKeyRevokedAt: now } : config))
          : current,
      );

      return { previous };
    },
    onError: (_error, _id, context) => {
      queryClient.setQueryData(queryKeys.agentConfigs.all(projectId), context?.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentConfigs.all(projectId) });
    },
  });
};
